import React, { forwardRef } from 'react';
import Loading from '../../common_components/Loading';
import { Warning } from '../../icons';
import {
  UNKNOWN_ERROR,
  UNKNOWN_PROCESSING_ERROR,
  WRONG_FORMAT,
  MISSING_VALUES,
  NOT_ENOUGH_ROWS,
  NOT_ENOUGH_TEXT_COLUMNS,
  NOT_ENOUGH_NUMBER_COLUMNS,
  NA_VALUES,
  FILE_TOO_BIG,
} from '../constants';

/**
 * Main purpose of this modal is to allow for changing
 * entire modal content without the need to close it
 * and open another (this open close operation when
 * done quickly causes an unpleasant glitch)
 */
export const Modal = forwardRef(function Modal(props, ref) {
  return (
    <dialog className="modal text-left not-prose" ref={ref}>
      {props.children}
    </dialog>
  );
});

export const LoadingModalBody = () => {
  return (
    <div className="modal-box">
      <Loading msg="Trwa wczytywanie pliku" />
    </div>
  );
};

export const WarningModalBody = ({ errorCode }) => {
  const title = modalTitles[errorCode] ?? modalTitles[UNKNOWN_ERROR];
  const body = modalBody[errorCode] ?? modalBody[UNKNOWN_ERROR];

  return (
    <div className="modal-box">
      <h3 className="font-bold text-xl sm:text-2xl text-warning mt-0">
        <Warning className={'inline-block mr-2 size-10'} /> {title}
      </h3>
      {body}
      <div className="modal-action">
        <form method="dialog">
          <button className="btn btn-secondary">Zamknij</button>
        </form>
      </div>
    </div>
  );
};

const modalTitles = {
  [WRONG_FORMAT]: 'Błędny format pliku',
  [FILE_TOO_BIG]: 'Zbyt duży rozmiar pliku',
  [MISSING_VALUES]: 'Wykryto brakujące wartości',
  [NOT_ENOUGH_ROWS]: 'Zbyt mała ilość danych',
  [NOT_ENOUGH_TEXT_COLUMNS]: 'Zbyt mała ilość danych',
  [NOT_ENOUGH_NUMBER_COLUMNS]: 'Zbyt mała ilość danych',
  [UNKNOWN_ERROR]: 'Błąd wczytywania danych',
  [UNKNOWN_PROCESSING_ERROR]: 'Błąd przetwarzania danych',
};
const modalBody = {
  [WRONG_FORMAT]: (
    <>
      <p className="pt-4 pb-4">
        Aplikacja obsługuje wyłącznie pliki w formatach: <strong>CSV</strong>{' '}
        (rozdzielane przecinkiem) i <strong>XLSX</strong> (Excel).
      </p>
      <p className="pb-4">
        Upewnij się, że wczytywany plik ma jeden z obsługiwanych formatów.
      </p>
    </>
  ),
  [FILE_TOO_BIG]: (
    <>
      <p className="pt-4 pb-4">Maksymalny możliwy rozmiar pliku to 2MB.</p>
      <p className="pb-4">
        Upewnij się, że wczytywany nie jest większy niż dopuszczalny rozmiar.
      </p>
    </>
  ),
  [MISSING_VALUES]: (
    <>
      <p className="pt-4 pb-4">
        Usuń wiersze zawierające puste komórki lub uzupełnij brakujące dane
        przed wczytaniem pliku.
      </p>
      <p className="pb-4">
        <strong>Uwaga!</strong> Wartości komórek {NA_VALUES.join(', ')} (w
        dowolnej wielkości liter) traktowane są jako oznaczenie braku danych.
      </p>
    </>
  ),
  [NOT_ENOUGH_ROWS]: (
    <>
      <p className="pt-4 pb-4">
        Abyśmy mogli przetworzyć Twoje dane muszą one mieć odpowiednią długość.
      </p>
      <p className="pb-4">
        Upewnij się, że Twoje dane mają co najmniej <u>6 rzędów</u>.
      </p>
    </>
  ),
  [NOT_ENOUGH_TEXT_COLUMNS]: (
    <>
      <p className="pt-4 pb-4">
        Abyśmy mogli przetworzyć Twoje dane muszą one posiadać odpowiednią ilość
        kolumn tekstowych.
      </p>
      <p className="pb-4">
        Upewnij się, że Twoje dane zawierają co najmniej{' '}
        <u>1 kolumnę tekstową</u>.
      </p>
    </>
  ),
  [NOT_ENOUGH_NUMBER_COLUMNS]: (
    <>
      <p className="pt-4 pb-4">
        Abyśmy mogli przetworzyć Twoje dane muszą one posiadać odpowiednią ilość
        kolumn liczbowych.
      </p>
      <p className="pb-4">
        Upewnij się, że Twoje dane zawierają co najmniej{' '}
        <u>3 kolumny liczbowe</u>.
      </p>
    </>
  ),
  [UNKNOWN_ERROR]: (
    <>
      <p className="pt-4 pb-4">
        Podczas wczytywania danych wystąpił nieoczekiwany błąd.
      </p>
      <p className="pb-4">
        Możesz spróbować wgrać inne dane, lub zwrócić się do nas po pomoc:
        <a
          className="ml-1 font-semibold whitespace-nowrap"
          href="mailto:ote-portal@agh.edu.pl"
        >
          ote-portal@agh.edu.pl
        </a>
      </p>
    </>
  ),
  [UNKNOWN_PROCESSING_ERROR]: (
    <>
      <p className="pt-4 pb-4">
        Aplikacja napotkała błędy podczas przetwarzania Twojego pliku. Przed
        ponownym wczytaniem danych, proszę sprawdzić:
        <ol className="pt-4 pb-4 pl-3">
          <li>1) Format pliku - czy plik ma rozszerzenie .csv lub .xlsx?</li>
          <li>2) Brakujące dane - czy wszystkie komórki zawierają wartości?</li>
          <li>3) Strukturę danych - czy dane są prawidłowo sformatowane?</li>
          <li>4) Typy danych - czy kolumny mają spójne formaty?</li>
        </ol>
        Po poprawieniu problemów, spróbuj wczytać plik ponownie.
      </p>
    </>
  ),
};
